import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLandlordStore, StaffRole, StaffStatus, LandlordStaff } from '@/stores/useLandlordStore';
import { ArrowLeft, Loader2, Mail, Briefcase, Shield, Phone, User, AlertCircle, Save, X } from 'lucide-react';
import { LoadingScreen } from '@/components/common/LoadingSpinner';
import { useToast } from '@/contexts/ToastContext';

const AVAILABLE_ROLES: StaffRole[] = ['Property Manager', 'Maintenance Staff', 'Accountant', 'Security', 'Admin'];
const AVAILABLE_STATUSES: StaffStatus[] = ['Active', 'Inactive', 'Pending', 'Suspended'];

interface FormErrors {
    staffEmail?: string;
    staffName?: string;
    staffPhone?: string;
}

export const EditStaff: React.FC = () => {
    const navigate = useNavigate();
    const { staffId } = useParams<{ staffId: string }>();
    const { staff, updateStaff, fetchStaff, properties, fetchProperties } = useLandlordStore();
    const { success, error: toastError } = useToast();
    
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [formErrors, setFormErrors] = useState<FormErrors>({});
    const [touched, setTouched] = useState<Record<string, boolean>>({});
    const [loading, setLoading] = useState(true);
    const [staffMember, setStaffMember] = useState<LandlordStaff | null>(null);

    const [formData, setFormData] = useState({
        staffName: '',
        staffEmail: '',
        staffPhone: '',
        role: 'Property Manager' as StaffRole,
        status: 'Active' as StaffStatus,
    });

    // Find staff member from store or load if needed
    useEffect(() => {
        if (staffId && staff.length > 0) {
            const member = staff.find(s => s.id === staffId);
            if (member) {
                setStaffMember(member);
                setFormData({
                    staffName: member.name || '',
                    staffEmail: member.email || '',
                    staffPhone: member.phone || '',
                    role: member.role as StaffRole,
                    status: member.status as StaffStatus,
                });
                setLoading(false);
            } else {
                // Try to fetch staff list again
                const storedPropertyId = localStorage.getItem('lastSelectedPropertyId');
                if (storedPropertyId) {
                    fetchStaff(storedPropertyId).then(() => {
                        const updatedMember = staff.find(s => s.id === staffId);
                        if (updatedMember) {
                            setStaffMember(updatedMember);
                            setFormData({
                                staffName: updatedMember.name || '',
                                staffEmail: updatedMember.email || '',
                                staffPhone: updatedMember.phone || '',
                                role: updatedMember.role as StaffRole,
                                status: updatedMember.status as StaffStatus,
                            });
                        }
                        setLoading(false);
                    });
                } else {
                    setLoading(false);
                }
            }
        }
        fetchProperties();
    }, [staffId, staff]);

    // Validation functions
    const validateEmail = (email: string): string | undefined => {
        if (!email) return 'Email is required';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) return 'Please enter a valid email address';
        return undefined;
    };

    const validateName = (name: string): string | undefined => {
        if (!name) return 'Name is required';
        if (name.length < 2) return 'Name must be at least 2 characters';
        if (name.length > 100) return 'Name must be less than 100 characters';
        return undefined;
    };

    const validatePhone = (phone: string): string | undefined => {
        if (!phone) return undefined; // Phone is optional
        const phoneRegex = /^[\d\s\-\+\(\)]{10,}$/;
        if (!phoneRegex.test(phone)) return 'Please enter a valid phone number';
        return undefined;
    };

    const handleBlur = (field: string) => {
        setTouched(prev => ({ ...prev, [field]: true }));
    };

    useEffect(() => {
        const errors: FormErrors = {};
        if (touched.staffEmail) errors.staffEmail = validateEmail(formData.staffEmail);
        if (touched.staffName) errors.staffName = validateName(formData.staffName);
        if (touched.staffPhone) errors.staffPhone = validatePhone(formData.staffPhone);
        setFormErrors(errors);
    }, [formData, touched]);

    const handlePermissionToggle = (permissionId: string) => {
        // This would be handled in StaffDetails/StaffPermissions page
    };

    const validateForm = (): boolean => {
        const errors: FormErrors = {};
        
        const emailError = validateEmail(formData.staffEmail);
        if (emailError) errors.staffEmail = emailError;
        
        const nameError = validateName(formData.staffName);
        if (nameError) errors.staffName = nameError;
        
        const phoneError = validatePhone(formData.staffPhone);
        if (phoneError) errors.staffPhone = phoneError;

        setFormErrors(errors);
        setTouched({
            staffEmail: true,
            staffName: true,
            staffPhone: true,
        });

        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!staffId) {
            setError('Staff ID is required');
            return;
        }

        if (!validateForm()) {
            setError('Please correct the errors below before submitting.');
            return;
        }

        setSubmitting(true);

        try {
            await updateStaff(staffId, {
                name: formData.staffName,
                email: formData.staffEmail,
                phone: formData.staffPhone,
                role: formData.role,
                status: formData.status,
            });
            success('Staff Updated', `${formData.staffName}'s information has been updated successfully.`);
            navigate('/landlord/staff');
        } catch (err: any) {
            setError(err.message || 'An error occurred while updating the staff member.');
            toastError('Update Failed', err.message || 'Could not update staff information.');
        } finally {
            setSubmitting(false);
        }
    };

    const getInputClasses = (hasError?: boolean) => {
        const baseClasses = "w-full pl-12 pr-4 py-3 rounded-xl border bg-light-input dark:bg-dark-input text-light-text dark:text-dark-text focus:ring-2 focus:ring-primary/50 outline-none font-medium transition-all";
        if (hasError) {
            return `${baseClasses} border-red-500 focus:ring-red-500/50`;
        }
        return `${baseClasses} border-light-border dark:border-dark-border`;
    };

    const renderFieldError = (error?: string) => {
        if (!error) return null;
        return (
            <div className="flex items-center gap-1 mt-1.5 text-red-600 dark:text-red-400 text-sm font-medium">
                <AlertCircle size={14} />
                {error}
            </div>
        );
    };

    if (loading) return <LoadingScreen />;

    if (!staffMember && !loading) {
        return (
            <div className="max-w-4xl mx-auto space-y-6 pb-24">
                <div className="bg-light-card dark:bg-dark-card p-6 rounded-xl shadow-sm border border-light-border dark:border-dark-border text-center">
                    <h2 className="text-xl font-bold text-light-text dark:text-dark-text mb-2">Staff Member Not Found</h2>
                    <p className="text-light-subtle dark:text-dark-subtle mb-4">The staff member you're looking for doesn't exist or has been removed.</p>
                    <button
                        onClick={() => navigate('/landlord/staff')}
                        className="px-6 py-2 bg-primary text-white rounded-xl font-bold hover:bg-primary/90 transition-colors"
                    >
                        Back to Staff List
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto space-y-6 pb-24">
            {/* Back Button Header */}
            <div className="flex items-center gap-4 bg-light-card dark:bg-dark-card p-4 rounded-xl shadow-sm border border-light-border dark:border-dark-border">
                <button
                    onClick={() => navigate('/landlord/staff')}
                    className="p-2 hover:bg-light-bg dark:hover:bg-dark-bg rounded-full transition-colors"
                >
                    <ArrowLeft size={20} className="text-light-subtle dark:text-dark-subtle" />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-light-text dark:text-dark-text flex items-center gap-2">
                        <Briefcase className="text-primary" size={24} />
                        Edit Staff Member
                    </h1>
                    <p className="text-sm text-light-subtle dark:text-dark-subtle mt-0.5">
                        Update information for {staffMember?.name}
                    </p>
                </div>
            </div>

            {/* Main Form */}
            <div className="bg-light-card dark:bg-dark-card p-6 rounded-xl shadow-sm border border-light-border dark:border-dark-border">
                {error && (
                    <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-xl text-sm border border-red-200 dark:border-red-800 font-medium flex items-center gap-2">
                        <AlertCircle size={18} />
                        {error}
                    </div>
                )}

                <form id="editStaffForm" onSubmit={handleSubmit} className="space-y-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Name */}
                        <div>
                            <label className="block text-sm font-bold text-light-text dark:text-dark-text mb-2">
                                Full Name <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={20} />
                                <input
                                    type="text"
                                    required
                                    placeholder="John Doe"
                                    value={formData.staffName}
                                    onChange={(e) => setFormData({ ...formData, staffName: e.target.value })}
                                    onBlur={() => handleBlur('staffName')}
                                    className={getInputClasses(!!formErrors.staffName)}
                                />
                            </div>
                            {renderFieldError(formErrors.staffName)}
                        </div>

                        {/* Email */}
                        <div>
                            <label className="block text-sm font-bold text-light-text dark:text-dark-text mb-2">
                                Email Address <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={20} />
                                <input
                                    type="email"
                                    required
                                    placeholder="staff@example.com"
                                    value={formData.staffEmail}
                                    onChange={(e) => setFormData({ ...formData, staffEmail: e.target.value })}
                                    onBlur={() => handleBlur('staffEmail')}
                                    className={getInputClasses(!!formErrors.staffEmail)}
                                />
                            </div>
                            {renderFieldError(formErrors.staffEmail)}
                        </div>

                        {/* Phone */}
                        <div>
                            <label className="block text-sm font-bold text-light-text dark:text-dark-text mb-2">
                                Phone Number <span className="text-light-subtle dark:text-dark-subtle font-normal">(Optional)</span>
                            </label>
                            <div className="relative">
                                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={20} />
                                <input
                                    type="tel"
                                    placeholder="+254 700 123 456"
                                    value={formData.staffPhone}
                                    onChange={(e) => setFormData({ ...formData, staffPhone: e.target.value })}
                                    onBlur={() => handleBlur('staffPhone')}
                                    className={getInputClasses(!!formErrors.staffPhone)}
                                />
                            </div>
                            {renderFieldError(formErrors.staffPhone)}
                        </div>

                        {/* Role */}
                        <div>
                            <label className="block text-sm font-bold text-light-text dark:text-dark-text mb-2">
                                Role <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <Shield className="absolute left-4 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={20} />
                                <select
                                    required
                                    value={formData.role}
                                    onChange={(e) => setFormData({ ...formData, role: e.target.value as StaffRole })}
                                    className="w-full pl-12 pr-4 py-3 rounded-xl border border-light-border dark:border-dark-border bg-light-input dark:bg-dark-input text-light-text dark:text-dark-text focus:ring-2 focus:ring-primary/50 outline-none font-medium"
                                >
                                    {AVAILABLE_ROLES.map(role => (
                                        <option key={role} value={role}>{role}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Status */}
                        <div>
                            <label className="block text-sm font-bold text-light-text dark:text-dark-text mb-2">
                                Status <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <Shield className="absolute left-4 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={20} />
                                <select
                                    required
                                    value={formData.status}
                                    onChange={(e) => setFormData({ ...formData, status: e.target.value as StaffStatus })}
                                    className="w-full pl-12 pr-4 py-3 rounded-xl border border-light-border dark:border-dark-border bg-light-input dark:bg-dark-input text-light-text dark:text-dark-text focus:ring-2 focus:ring-primary/50 outline-none font-medium"
                                >
                                    {AVAILABLE_STATUSES.map(status => (
                                        <option key={status} value={status}>{status}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Current Assignment Info */}
                    {staffMember && (
                        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                            <h3 className="text-sm font-bold text-light-text dark:text-dark-text mb-2">Current Assignment</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <div>
                                    <span className="text-light-subtle dark:text-dark-subtle">Property:</span>
                                    <span className="ml-2 font-medium text-light-text dark:text-dark-text">
                                        {staffMember.property_name || 'Not assigned'}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-light-subtle dark:text-dark-subtle">Staff ID:</span>
                                    <span className="ml-2 font-medium text-light-text dark:text-dark-text">
                                        {staffMember.id.substring(0, 8)}...
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-between gap-4 pt-6 mt-8 border-t border-light-border dark:border-dark-border">
                        <button
                            type="button"
                            onClick={() => navigate('/landlord/staff')}
                            disabled={submitting}
                            className="flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-light-subtle dark:text-dark-subtle hover:bg-light-input dark:hover:bg-dark-input border border-light-border dark:border-dark-border transition-colors disabled:opacity-50"
                        >
                            <X size={18} />
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="flex items-center gap-2 bg-primary text-white px-8 py-3 rounded-xl font-bold hover:bg-primary/90 transition-all shadow-md disabled:opacity-50 disabled:shadow-none"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 size={20} className="animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save size={20} />
                                    Save Changes
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};